import { useState, useEffect, useCallback, useRef } from "react";
import { useSeoMeta } from "@unhead/react";
import { Shield, ShieldAlert, Globe, MapPin, Building2, Monitor, Eye, EyeOff, ArrowRightLeft, ArrowRight, Loader2, Network, AlertTriangle } from "lucide-react";
import { probeWithFallback, probeWithCustomFetch, getUserAgent, type ProbeResult } from "@/lib/probe";
import { bootstrapNym, getNymMixFetch, isNymReady, getNymState, resetNymClient } from "@/lib/nym";

type Mode = "direct" | "nym";
type ConnectionPhase = "idle" | "downloading" | "connecting" | "probing" | "ready" | "error";

interface ProbeDataSet {
  direct: ProbeResult | null;
  nym: ProbeResult | null;
}

const HONESTY_PANEL_ITEMS = [
  "Only HTTP requests made by this page are routed through the Nym mixnet.",
  "This page itself loaded over your normal connection — flipping the switch does not change that.",
  "WebSocket, WebRTC, and DNS traffic are NOT routed through Nym.",
  "This is a demonstration, not ongoing protection for your general browsing.",
  "Your current VPN or Tor exit is what \"Direct\" shows — it reflects wherever your browser connects from.",
];

const STATUS_BADGE = {
  direct: {
    label: "Exposed",
    icon: ShieldAlert,
    color: "text-[var(--color-exposed)]",
    bg: "bg-[var(--color-exposed)]/10",
    border: "border-[var(--color-exposed)]/30",
  },
  nym: {
    label: "Mixnet-Protected (HTTP layer)",
    icon: Shield,
    color: "text-[var(--color-protected)]",
    bg: "bg-[var(--color-protected)]/10",
    border: "border-[var(--color-protected)]/30",
  },
};

function formatField(key: string): { label: string; icon: typeof Globe } {
  switch (key) {
    case "ip": return { label: "IP Address", icon: Globe };
    case "location": return { label: "Location", icon: MapPin };
    case "isp": return { label: "Network Operator", icon: Building2 };
    case "userAgent": return { label: "User Agent", icon: Monitor };
    default: return { label: key, icon: Globe };
  }
}

interface FieldRowProps {
  label: string;
  icon: typeof Globe;
  directValue: string;
  nymValue: string | null;
  mode: Mode;
  changed: boolean;
  obscured: boolean;
}

function FieldRow({ label, icon: Icon, directValue, nymValue, mode, changed, obscured }: FieldRowProps) {
  const displayValue = mode === "direct" ? directValue : (nymValue ?? directValue);
  const isChanged = mode === "nym" && changed;
  const isObscured = mode === "nym" && obscured;

  return (
    <div className={`flex items-start gap-3 px-4 py-3 rounded-lg transition-all duration-500 ${
      isChanged ? "animate-pulse-highlight bg-[var(--color-protected)]/10" : ""
    } ${isObscured ? "opacity-70" : ""}`}>
      <Icon className="w-4 h-4 mt-0.5 text-[var(--color-dim)] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-[var(--color-text-dim)] uppercase tracking-wider">{label}</div>
        <div className={`font-mono text-sm mt-0.5 break-all ${
          isObscured ? "text-[var(--color-text-dim)] italic" : "text-[var(--color-text-primary)]"
        }`}>
          {isObscured ? "Not forwarded (hidden by mixnet)" : displayValue}
        </div>
      </div>
      {mode === "nym" && isChanged && (
        <div className="shrink-0">
          <EyeOff className="w-4 h-4 text-[var(--color-protected)]" />
        </div>
      )}
    </div>
  );
}

function ToggleSwitch({
  mode,
  disabled,
  onClick,
}: {
  mode: Mode;
  disabled: boolean;
  onClick: () => void;
}) {
  const isNym = mode === "nym";

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isNym}
      aria-label={`Switch to ${isNym ? "Direct" : "Via Nym Mixnet"} mode`}
      disabled={disabled}
      onClick={onClick}
      className={`relative w-[280px] h-[56px] rounded-full transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-cyan)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-dark)] ${
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      } ${isNym ? "bg-[var(--color-protected)]" : "bg-[var(--color-exposed)]"}`}
    >
      {/* Labels */}
      <span
        className={`absolute left-6 top-1/2 -translate-y-1/2 text-sm font-semibold transition-all duration-300 z-10 ${
          isNym ? "text-white/60" : "text-white"
        }`}
      >
        Direct
      </span>
      <span
        className={`absolute right-6 top-1/2 -translate-y-1/2 text-sm font-semibold transition-all duration-300 z-10 ${
          isNym ? "text-white" : "text-white/60"
        }`}
      >
        Via Nym Mixnet
      </span>

      {/* Knob */}
      <div
        className={`absolute top-1 w-[138px] h-[48px] bg-white rounded-full shadow-lg transition-all duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
          isNym ? "left-[140px]" : "left-1"
        }`}
      >
        <div className="flex items-center justify-center h-full">
          {isNym ? (
            <ArrowRight className="w-5 h-5 text-[var(--color-protected)]" />
          ) : (
            <ArrowRightLeft className="w-5 h-5 text-[var(--color-exposed)]" />
          )}
        </div>
      </div>

      {/* Notch decoration */}
      <div className="absolute left-[138px] top-1/2 -translate-y-1/2 w-[4px] h-[20px] bg-white/20 rounded-full" />
    </button>
  );
}

function RoutingDiagram({ mode }: { mode: Mode }) {
  const [animPhase, setAnimPhase] = useState(0);

  useEffect(() => {
    if (mode === "nym") {
      // Animate through the hops sequentially
      const t1 = setTimeout(() => setAnimPhase(1), 200);
      const t2 = setTimeout(() => setAnimPhase(2), 500);
      const t3 = setTimeout(() => setAnimPhase(3), 800);
      const t4 = setTimeout(() => setAnimPhase(4), 1100);
      const t5 = setTimeout(() => setAnimPhase(5), 1400);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
        clearTimeout(t4);
        clearTimeout(t5);
      };
    } else {
      setAnimPhase(0);
    }
  }, [mode]);

  const Node = ({ label, active, glow, idx }: { label: string; active: boolean; glow?: boolean; idx?: number }) => (
    <div
      className={`flex flex-col items-center gap-1 transition-all duration-500 ${
        active ? "opacity-100 scale-100" : "opacity-0 scale-75"
      } ${glow ? "animate-hop-glow" : ""}`}
      style={idx !== undefined ? { transitionDelay: `${idx * 300}ms` } : undefined}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
        active && glow
          ? "border-[var(--color-protected)] bg-[var(--color-protected)]/15"
          : active
            ? mode === "direct"
              ? "border-[var(--color-exposed)] bg-[var(--color-exposed)]/15"
              : "border-[var(--color-accent-cyan)] bg-[var(--color-accent-cyan)]/10"
            : "border-[var(--color-dim)]/30 bg-transparent"
      }`}>
        {idx === 0 || idx === 4 ? (
          <Monitor className={`w-4 h-4 ${active ? (glow ? "text-[var(--color-protected)]" : "text-[var(--color-text-primary)]") : "text-[var(--color-dim)]/30"}`} />
        ) : (
          <Network className={`w-4 h-4 ${active ? "text-[var(--color-accent-cyan)]" : "text-[var(--color-dim)]/30"}`} />
        )}
      </div>
      <span className={`text-[10px] font-mono text-center leading-tight max-w-[70px] transition-all duration-500 ${
        active ? "text-[var(--color-text-secondary)]" : "text-[var(--color-text-dim)]/30"
      }`}>
        {label}
      </span>
    </div>
  );

  const Connector = ({ active, idx }: { active: boolean; idx?: number }) => (
    <div
      className={`w-8 h-[2px] transition-all duration-500 ${
        active ? "bg-[var(--color-accent-cyan)]/50" : "bg-[var(--color-dim)]/15"
      }`}
      style={idx !== undefined ? { transitionDelay: `${idx * 300 + 150}ms` } : undefined}
    />
  );

  return (
    <div className="mt-6">
      <div className="flex items-center justify-center gap-1 px-2 overflow-x-auto">
        {mode === "direct" ? (
          <>
            <Node label="You" active active idx={0} />
            <div className="flex items-center">
              <Connector active />
              <Eye className="w-3 h-3 text-[var(--color-exposed)] -mx-1 z-10" />
              <Connector active />
            </div>
            <Node label="Server" active />
          </>
        ) : (
          <>
            <Node label="You" active={animPhase >= 0} idx={0} />
            <Connector active={animPhase >= 1} idx={0} />
            <Node label="Entry" active={animPhase >= 1} idx={1} />
            <Connector active={animPhase >= 2} idx={1} />
            <Node label="Mix 1" active={animPhase >= 2} idx={2} />
            <Connector active={animPhase >= 3} idx={2} />
            <Node label="Mix 2" active={animPhase >= 3} idx={3} />
            <Connector active={animPhase >= 3} idx={3} />
            <Node label="Mix 3" active={animPhase >= 4} idx={4} />
            <Connector active={animPhase >= 4} idx={4} />
            <Node label="Exit" active={animPhase >= 4} idx={5} />
            <div className="flex items-center">
              <Connector active={animPhase >= 5} idx={5} />
              <EyeOff className="w-3 h-3 text-[var(--color-protected)] -mx-1 z-10" />
              <Connector active={animPhase >= 5} idx={5} />
            </div>
            <Node label="Server" active={animPhase >= 5} idx={6} glow />
          </>
        )}
      </div>

      {/* Annotation */}
      <div className="mt-3 text-center">
        {mode === "direct" ? (
          <p className="text-xs text-[var(--color-dim)] italic">
            Your connection is directly visible — every hop between you and the server can be traced.
          </p>
        ) : (
          <p className="text-xs text-[var(--color-dim)] italic max-w-md mx-auto">
            Each node only knows its immediate neighbors. No single node knows the full path or can link you to the server.
          </p>
        )}
      </div>

      {/* Legend for nym mode */}
      {mode === "nym" && (
        <div className="flex items-center justify-center gap-4 mt-2">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full border-2 border-[var(--color-accent-cyan)] bg-[var(--color-accent-cyan)]/10" />
            <span className="text-[10px] font-mono text-[var(--color-text-dim)]">Mix node</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full border-2 border-[var(--color-protected)] bg-[var(--color-protected)]/15" />
            <span className="text-[10px] font-mono text-[var(--color-text-dim)]">Exit / origin</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ConnectingState({ phase }: { phase: ConnectionPhase }) {
  const messages: Record<ConnectionPhase, string> = {
    idle: "",
    downloading: "Downloading mixnet client (~18 MB)…",
    connecting: "Connecting to mixnet gateway (10-30s)…",
    probing: "Running probe through mixnet…",
    ready: "Connected!",
    error: "Connection failed",
  };

  const substeps = [
    { key: "download", label: "Download bundle", phase: "downloading" },
    { key: "connect", label: "Gateway handshake", phase: "connecting" },
    { key: "probe", label: "Probe through mixnet", phase: "probing" },
  ] as const;

  const isActive = (stepPhase: string) => {
    const order = ["downloading", "connecting", "probing", "ready"];
    return order.indexOf(stepPhase) <= order.indexOf(phase) && phase !== "idle" && phase !== "error";
  };

  return (
    <div className="flex flex-col items-center gap-4 py-6 animate-fade-in">
      <Loader2 className="w-8 h-8 text-[var(--color-accent-cyan)] animate-spin" />
      <p className="text-sm text-[var(--color-text-secondary)]">{messages[phase]}</p>
      <div className="flex flex-col gap-2 w-full max-w-[280px]">
        {substeps.map((step) => (
          <div key={step.key} className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full transition-all duration-500 ${
              isActive(step.phase)
                ? phase === step.phase
                  ? "bg-[var(--color-accent-cyan)] animate-progress-pulse"
                  : phase === "ready"
                    ? "bg-[var(--color-protected)]"
                    : "bg-[var(--color-protected)]"
                : "bg-[var(--color-dim)]/30"
            }`} />
            <span className={`text-xs transition-all duration-500 ${
              isActive(step.phase) ? "text-[var(--color-text-secondary)]" : "text-[var(--color-text-dim)]/40"
            }`}>
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 py-6 animate-fade-in">
      <AlertTriangle className="w-8 h-8 text-[var(--color-exposed)]" />
      <p className="text-sm text-[var(--color-text-secondary)] text-center max-w-sm">{message}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-[var(--color-exposed)] hover:opacity-90 transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-cyan)]"
      >
        Retry
      </button>
    </div>
  );
}

function CompareStrip({
  directResult,
  nymResult,
}: {
  directResult: ProbeResult | null;
  nymResult: ProbeResult | null;
}) {
  if (!directResult || !nymResult) return null;

  return (
    <div className="mt-6 border border-[var(--color-border)]/30 rounded-xl overflow-hidden">
      <div className="grid grid-cols-2 divide-x divide-[var(--color-border)]/30">
        <div className="p-4 bg-[var(--color-exposed)]/5">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="w-4 h-4 text-[var(--color-exposed)]" />
            <span className="text-xs font-semibold text-[var(--color-exposed)] uppercase tracking-wider">Direct</span>
          </div>
          <div className="space-y-1.5">
            <div className="text-xs text-[var(--color-text-dim)]">IP: <span className="font-mono text-[var(--color-text-primary)]">{directResult.ip}</span></div>
            <div className="text-xs text-[var(--color-text-dim)]">Location: <span className="text-[var(--color-text-primary)]">{directResult.city}, {directResult.country}</span></div>
          </div>
        </div>
        <div className="p-4 bg-[var(--color-protected)]/5">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-[var(--color-protected)]" />
            <span className="text-xs font-semibold text-[var(--color-protected)] uppercase tracking-wider">Via Nym</span>
          </div>
          <div className="space-y-1.5">
            <div className="text-xs text-[var(--color-text-dim)]">IP: <span className="font-mono text-[var(--color-text-primary)]">{nymResult.ip}</span></div>
            <div className="text-xs text-[var(--color-text-dim)]">Location: <span className="text-[var(--color-text-primary)]">{nymResult.city}, {nymResult.country}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Index() {
  useSeoMeta({
    title: "Privacy Switch — See what the web sees about you",
    description: "Flip the switch to see the difference Nym's mixnet makes. A live demo of what your browser reveals.",
  });

  const [mode, setMode] = useState<Mode>("direct");
  const [connectionPhase, setConnectionPhase] = useState<ConnectionPhase>("idle");
  const [probeData, setProbeData] = useState<ProbeDataSet>({ direct: null, nym: null });
  const [probeError, setProbeError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCompare, setShowCompare] = useState(false);

  // Highlight tracking for field changes
  const [changedFields, setChangedFields] = useState<Set<string>>(new Set());
  const prevNymResult = useRef<ProbeResult | null>(null);

  const userAgent = getUserAgent();

  // Detect which IP probe services work
  const runDirectProbe = useCallback(async () => {
    try {
      setProbeError(null);
      const result = await probeWithFallback();
      setProbeData((prev) => ({ ...prev, direct: result }));
      return result;
    } catch (e) {
      setProbeError((e as Error).message);
      return null;
    }
  }, []);

  // Initial probe on mount
  useEffect(() => {
    runDirectProbe().finally(() => setLoading(false));
  }, [runDirectProbe]);

  const handleToggle = useCallback(async () => {
    if (mode === "direct") {
      // Switch to Nym mode
      if (!isNymReady()) {
        setConnectionPhase("downloading");

        // Set a 90-second overall timeout for the entire Nym bootstrap
        const timeoutId = setTimeout(() => {
          resetNymClient();
          if (connectionPhase !== "error" && connectionPhase !== "idle") {
            setConnectionPhase("error");
            setProbeError("Mixnet bootstrap timed out after 90 seconds. The Nym gateway may be unreachable from your location.");
          }
        }, 90000);

        try {
          const mixFetch = await bootstrapNym((msg) => {
            if (msg.includes("Downloading")) setConnectionPhase("downloading");
            else if (msg.includes("Connecting") || msg.includes("gateway")) setConnectionPhase("connecting");
          });
          clearTimeout(timeoutId);
          setConnectionPhase("probing");

          // Run the probe through the mixnet
          const probeResult = await probeWithCustomFetch(mixFetch);

          if (probeResult) {
            // Track changed fields
            const direct = probeData.direct;
            if (direct) {
              const changed = new Set<string>();
              if (probeResult.ip !== direct.ip) changed.add("ip");
              if (probeResult.city !== direct.city || probeResult.country !== direct.country) changed.add("location");
              if (probeResult.isp !== direct.isp || probeResult.org !== direct.org) changed.add("isp");
              // User agent is always obscured in nym mode
              changed.add("userAgent");
              setChangedFields(changed);
            }
            setProbeData((prev) => ({ ...prev, nym: probeResult }));
          } else {
            setProbeError("Probe through mixnet returned no data");
          }

          setConnectionPhase("ready");
          setMode("nym");
          setShowCompare(true);

          // Reset phase after a moment
          setTimeout(() => setConnectionPhase("idle"), 1000);
        } catch (e) {
          clearTimeout(timeoutId);
          setConnectionPhase("error");
          const msg = (e as Error).message;
          setProbeError(msg.includes("mixnet") ? `Mixnet error: ${msg}` : `Failed: ${msg}`);
          resetNymClient();
        }
      } else {
        // Client is already initialized — just run the probe
        setConnectionPhase("probing");
        try {
          const mixFetch = getNymMixFetch();
          if (!mixFetch) throw new Error("Mixnet client not available");
          const result = await probeWithCustomFetch(mixFetch);

          if (result) {
            const direct = probeData.direct;
            if (direct) {
              const changed = new Set<string>();
              if (result.ip !== direct.ip) changed.add("ip");
              if (result.city !== direct.city || result.country !== direct.country) changed.add("location");
              if (result.isp !== direct.isp || result.org !== direct.org) changed.add("isp");
              changed.add("userAgent");
              setChangedFields(changed);
            }
            setProbeData((prev) => ({ ...prev, nym: result }));
          }
          setMode("nym");
          setShowCompare(true);
          setConnectionPhase("idle");
        } catch (e) {
          setConnectionPhase("error");
          setProbeError((e as Error).message);
        }
      }
    } else {
      // Switch back to Direct mode
      setConnectionPhase("idle");
      setMode("direct");
      setChangedFields(new Set());
      const result = await runDirectProbe();
      if (result) {
        setShowCompare(true);
      }
    }
  }, [mode, probeData.direct, runDirectProbe]);

  const handleRetryNym = useCallback(async () => {
    resetNymClient();
    setProbeError(null);
    setConnectionPhase("idle");
  }, []);

  const currentResult = mode === "direct" ? probeData.direct : (probeData.nym ?? probeData.direct);
  const badge = mode === "direct" ? STATUS_BADGE.direct : STATUS_BADGE.nym;
  const BadgeIcon = badge.icon;

  // Prepare field values
  const directResult = probeData.direct;

  // Reset changed fields after animation
  useEffect(() => {
    if (changedFields.size > 0) {
      const timer = setTimeout(() => setChangedFields(new Set()), 2000);
      return () => clearTimeout(timer);
    }
  }, [changedFields]);

  return (
    <div className="min-h-dvh bg-[var(--color-bg-dark)] text-[var(--color-text-primary)]">
      {/* Main content */}
      <div className="max-w-2xl mx-auto px-4 py-8 md:py-16">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            Privacy Switch
          </h1>
          <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
            See what the web sees — and what it doesn't when you use the Nym mixnet.
          </p>
          <p className="mt-1 text-xs text-[var(--color-text-dim)]">
            Vibed with{" "}
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-[var(--color-accent-cyan)] transition-colors"
            >
              Shakespeare
            </a>
          </p>
        </div>

        {/* Status Badge */}
        {currentResult && !loading && (
          <div className={`flex items-center justify-center gap-2 px-4 py-2 rounded-full ${badge.bg} ${badge.border} border w-fit mx-auto mb-6`}>
            <BadgeIcon className={`w-5 h-5 ${badge.color}`} />
            <span className={`text-sm font-semibold ${badge.color}`}>{badge.label}</span>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-[var(--color-accent-cyan)] animate-spin" />
            <span className="ml-3 text-sm text-[var(--color-text-secondary)]">Running probe…</span>
          </div>
        )}

        {/* Error state */}
        {probeError && !loading && (
          <div className="mb-6 p-4 rounded-xl border border-[var(--color-exposed)]/30 bg-[var(--color-exposed)]/5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[var(--color-exposed)] shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-[var(--color-exposed)]">Probe Error</p>
                <p className="text-xs text-[var(--color-text-secondary)] mt-1">{probeError}</p>
                {connectionPhase === "error" && (
                  <button
                    onClick={handleRetryNym}
                    className="mt-3 text-xs font-medium text-[var(--color-accent-cyan)] hover:underline"
                  >
                    Retry mixnet connection
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Connecting state */}
        {connectionPhase !== "idle" && connectionPhase !== "ready" && (
          <div className="mb-6">
            {connectionPhase === "error" ? (
              <ErrorState
                message={probeError ?? "Could not establish a mixnet route."}
                onRetry={handleRetryNym}
              />
            ) : (
              <ConnectingState phase={connectionPhase} />
            )}
          </div>
        )}

        {/* COI Not Available Warning */}
        {(() => {
          const state = getNymState();
          if (state.status === "no_coi") return (
            <div className="mb-6 p-4 rounded-xl border border-[var(--color-accent-cyan)]/30 bg-[var(--color-accent-cyan)]/5">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-[var(--color-accent-cyan)] shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[var(--color-accent-cyan)]">Mixnet Unavailable</p>
                  <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                    The Nym mixnet client requires cross-origin isolation, which needs specific HTTP headers from the hosting server.
                  </p>
                  <p className="text-xs text-[var(--color-text-dim)] mt-2">
                    Deploy this page to a host that sends:<br />
                    <code className="text-[var(--color-accent-cyan)]">Cross-Origin-Opener-Policy: same-origin</code><br />
                    <code className="text-[var(--color-accent-cyan)]">Cross-Origin-Embedder-Policy: require-corp</code>
                  </p>
                </div>
              </div>
            </div>
          );
          return null;
        })()}

        {/* The Switch */}
        <div className="flex justify-center mb-8">
          <ToggleSwitch
            mode={mode}
            disabled={loading || connectionPhase === "downloading" || connectionPhase === "connecting" || connectionPhase === "probing"}
            onClick={handleToggle}
          />
        </div>

        {/* Results Card */}
        {currentResult && (
          <div className="border border-[var(--color-border)]/20 rounded-xl overflow-hidden mb-6">
            <div className="px-4 py-3 bg-[var(--color-bg-card)] border-b border-[var(--color-border)]/20">
              <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
                What this server sees
              </h2>
              <p className="text-xs text-[var(--color-text-dim)] mt-0.5">
                {mode === "direct"
                  ? "This is what the probe endpoint learns about your connection."
                  : "The endpoint sees the exit gateway, not you."}
              </p>
            </div>
            <div className="divide-y divide-[var(--color-border)]/10 bg-[var(--color-bg-card)]">
              {/* IP */}
              <FieldRow
                label="IP Address"
                icon={Globe}
                directValue={directResult?.ip ?? "—"}
                nymValue={probeData.nym?.ip ?? null}
                mode={mode}
                changed={changedFields.has("ip")}
                obscured={mode === "nym" && probeData.nym !== null && directResult?.ip !== probeData.nym?.ip}
              />

              {/* Location */}
              <FieldRow
                label="Location"
                icon={MapPin}
                directValue={directResult ? `${directResult.city}, ${directResult.country}` : "—"}
                nymValue={probeData.nym ? `${probeData.nym.city}, ${probeData.nym.country}` : null}
                mode={mode}
                changed={changedFields.has("location")}
                obscured={mode === "nym" && probeData.nym !== null}
              />

              {/* Network Operator */}
              <FieldRow
                label="Network Operator"
                icon={Building2}
                directValue={directResult?.isp ?? directResult?.org ?? "—"}
                nymValue={probeData.nym?.isp ?? probeData.nym?.org ?? null}
                mode={mode}
                changed={changedFields.has("isp")}
                obscured={mode === "nym" && probeData.nym !== null}
              />

              {/* User Agent */}
              <FieldRow
                label="User Agent"
                icon={Monitor}
                directValue={userAgent}
                nymValue={null}
                mode={mode}
                changed={changedFields.has("userAgent")}
                obscured={mode === "nym"}
              />
            </div>
          </div>
        )}

        {/* Compare strip */}
        {showCompare && probeData.direct && probeData.nym && (
          <CompareStrip directResult={probeData.direct} nymResult={probeData.nym} />
        )}

        {/* Routing Diagram */}
        <div className="mt-8 p-6 rounded-xl border border-[var(--color-border)]/15 bg-[var(--color-bg-card)]">
          <h3 className="text-xs font-semibold text-[var(--color-text-dim)] uppercase tracking-wider mb-4">
            Routing Path
          </h3>
          <RoutingDiagram mode={mode} />
        </div>

        {/* Honesty Panel */}
        <div className="mt-8 p-6 rounded-xl border border-[var(--color-border)]/15 bg-[var(--color-bg-card)]">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-[var(--color-dim)]" />
            <h3 className="text-xs font-semibold text-[var(--color-text-dim)] uppercase tracking-wider">
              What this demo does — and doesn't — protect
            </h3>
          </div>
          <ul className="space-y-2">
            {HONESTY_PANEL_ITEMS.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-[var(--color-text-secondary)]">
                <span className="text-[var(--color-dim)] mt-0.5">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t border-[var(--color-border)]/10">
            <p className="text-xs text-[var(--color-text-dim)]">
              For real, ongoing protection, check out{" "}
              <a
                href="https://nym.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--color-accent-cyan)] underline hover:no-underline"
              >
                NymVPN
              </a>{" "}
              and the{" "}
              <a
                href="https://nym.com/mixnet"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--color-accent-cyan)] underline hover:no-underline"
              >
                Nym project
              </a>
              .
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-[var(--color-text-dim)]">
          <p>This page does not collect, store, or transmit your data to any operator-controlled server.</p>
        </div>
      </div>
    </div>
  );
}
